import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { initReferralTracker, setPartnerSlug } from "@/lib/useReferralTracker";
import { initAnalytics, useAnalyticsRouter } from "@/lib/useAnalyticsTracker";
import Login from "@/pages/Login";
import ReferralStatus from "@/pages/ReferralStatus";
import TrafficAnalyticsDashboard from "@/pages/TrafficAnalyticsDashboard";
import Dashboard from "@/pages/Dashboard";
import TripChat from "@/pages/TripChat";
import Settings from "@/pages/Settings";
import Briefs from "@/pages/Briefs";
import Landing from "@/pages/Landing";
import AdminAnalytics from "@/pages/AdminAnalytics";
import AdminExtensionConfig from "@/pages/AdminExtensionConfig";
import AdminMasterList from "@/pages/AdminMasterList";
import AdminRefData from "@/pages/AdminRefData";
import AdminDeals from "@/pages/AdminDeals";
import AdminDealsUsers from "@/pages/AdminDealsUsers";
import AdminBilling from "@/pages/AdminBilling";
import AdminLLMUsage from "@/pages/AdminLLMUsage";
import AdminNetTrace from "@/pages/AdminNetTrace";
import AdminPartners from "@/pages/AdminPartners";
import AdminSettings from "@/pages/AdminSettings";
import PartnerDashboard from "@/pages/PartnerDashboard";
import Privacy from "@/pages/Privacy";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import "@/App.css";

// deals_user role is scoped to the admin Deals page only — bounce them off
// every other authenticated route to /admin/deals. Admins always pass through.
function isDealsOnly(user) {
  return user && !user.is_admin && user.role === "deals_user";
}

// partner_admin role is scoped to /partner + /admin/deals. Same bounce
// pattern as deals_user, but the landing page is the partner dashboard.
function isPartnerAdmin(user) {
  return user && !user.is_admin && user.role === "partner_admin";
}

// Tiny inline component that captures the partner slug from /r/:slug into
// localStorage on mount, then redirects the visitor to the normal Landing
// page where the partner banner / logo will render.
function PartnerSlugCapture() {
  const { slug } = useParams();
  useEffect(() => {
    if (slug) setPartnerSlug(slug);
  }, [slug]);
  return <Navigate to="/" replace />;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="spinner" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // deals_user is locked to /admin/deals. partner_admin is a regular
  // user with extras (Deals + Referral Dashboard) — let them through.
  if (isDealsOnly(user)) return <Navigate to="/admin/deals" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="spinner" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (isDealsOnly(user)) return <Navigate to="/admin/deals" replace />;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;
  return children;
}

function DealsRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="spinner" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (
    !user.is_admin
    && user.role !== "deals_user"
    && user.role !== "partner_admin"
  ) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function PartnerRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="spinner" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin && user.role !== "partner_admin") {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function AppRouter() {
  useAnalyticsRouter();
  return (
    <>
    <ImpersonationBanner />
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/plan"
        element={
          <ProtectedRoute>
            <TripChat />
          </ProtectedRoute>
        }
      />
      <Route
        path="/plan/:id"
        element={
          <ProtectedRoute>
            <TripChat />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/briefs"
        element={
          <ProtectedRoute>
            <Briefs />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminAnalytics />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/extension-config"
        element={
          <AdminRoute>
            <AdminExtensionConfig />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/master-list"
        element={
          <AdminRoute>
            <AdminMasterList />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/refdata"
        element={
          <AdminRoute>
            <AdminRefData />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/deals"
        element={
          <DealsRoute>
            <AdminDeals />
          </DealsRoute>
        }
      />
      <Route
        path="/admin/deals-users"
        element={
          <AdminRoute>
            <AdminDealsUsers />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/billing"
        element={
          <AdminRoute>
            <AdminBilling />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/llm-usage"
        element={
          <AdminRoute>
            <AdminLLMUsage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/partners"
        element={
          <AdminRoute>
            <AdminPartners />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/net-trace"
        element={
          <AdminRoute>
            <AdminNetTrace />
          </AdminRoute>
        }
      />
      <Route
        path="/partner"
        element={
          <PartnerRoute>
            <PartnerDashboard />
          </PartnerRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <AdminRoute>
            <AdminSettings />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/traffic"
        element={
          <AdminRoute>
            <TrafficAnalyticsDashboard />
          </AdminRoute>
        }
      />
      <Route path="/privacy-policy" element={<Privacy />} />
      <Route path="/i/:code" element={<ReferralStatus />} />
      <Route path="/r/:slug" element={<PartnerSlugCapture />} />
      <Route path="/" element={<Landing />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

export default function App() {
  useEffect(() => {
    initReferralTracker();
    initAnalytics();
  }, []);
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </AuthProvider>
  );
}
